import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

// Internal email derivation: login_id "GTB01" -> "gtb01-abc123@gtbtire.app"
// Suffix ensures login_id can be reused after admin delete (since orphan auth.users
// rows can't be removed without the service role key).
const sanitize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const randomSuffix = () => Math.random().toString(36).slice(2, 8);
export const buildInternalEmail = (loginId) => `${sanitize(loginId)}-${randomSuffix()}@gtbtire.app`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminExists, setAdminExists] = useState(null); // null=unknown, true/false=resolved

  useEffect(() => {
    checkAdminExists();

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminExists = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_exists');
      if (error) {
        // Migration not yet applied — treat as legacy mode (admin exists = true so login flow runs)
        console.warn('admin_exists RPC unavailable:', error.message);
        setAdminExists(true);
        return;
      }
      setAdminExists(Boolean(data));
    } catch (err) {
      console.error('admin_exists error:', err);
      setAdminExists(true);
    }
  };

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*, companies(*)')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.error('Profile fetch error:', error);
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  // Login by login_id (not email). Returns { error } or { data }.
  const signIn = async (loginId, password) => {
    if (!loginId?.trim() || !password) {
      return { error: { message: 'ID dan password wajib diisi' } };
    }
    const { data: email, error: rpcErr } = await supabase.rpc('get_login_email', {
      p_login_id: loginId.trim(),
    });
    if (rpcErr || !email) {
      return { error: { message: 'ID tidak ditemukan. Hubungi administrator.' } };
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { error: { message: 'Password salah. Hubungi administrator.' } };
    }
    return { data };
  };

  // First-time admin setup. Called from SetupAdmin page.
  // Uses normal supabase client because we WANT the admin to be signed in after
  // this completes (no second user in the mix).
  const setupAdmin = async (loginId, password) => {
    const email = buildInternalEmail(loginId);
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error };
    if (!data.session && !data.user) {
      return { error: { message: 'Email confirmation aktif di Supabase. Matikan di Auth Settings.' } };
    }
    // If email confirmation is disabled, session exists. If not, we still have user but no session.
    const userId = data.user?.id;
    if (!userId) return { error: { message: 'Auth signup tidak mengembalikan user ID' } };

    // Wait briefly for session to be applied (signInWithPassword.onAuthStateChange races)
    if (!data.session) {
      const signIn = await supabase.auth.signInWithPassword({ email, password });
      if (signIn.error) return { error: signIn.error };
    }

    const { error: rpcErr } = await supabase.rpc('setup_create_admin', {
      p_user_id: userId,
      p_login_id: loginId,
      p_email: email,
    });
    if (rpcErr) return { error: rpcErr };
    await fetchProfile(userId);
    setAdminExists(true);
    return { data };
  };

  // Admin creates a customer user via a single SECURITY DEFINER RPC that
  // handles auth.users + auth.identities + companies + users in one transaction.
  // This avoids any client-side conflict ("body stream already read" etc.).
  const adminCreateCustomer = async ({ loginId, password, companyName }) => {
    if (!loginId?.trim() || !password || !companyName?.trim()) {
      return { error: { message: 'Semua field wajib diisi' } };
    }
    const { data, error } = await supabase.rpc('admin_create_user_full', {
      p_login_id: loginId.trim(),
      p_password: password,
      p_company_name: companyName.trim(),
    });
    if (error) return { error };
    return { data: { user_id: data, login_id: loginId.trim() } };
  };

  const adminListUsers = async () => {
    const { data, error } = await supabase.rpc('admin_list_users');
    return { data, error };
  };

  const adminDeleteUser = async (userId) => {
    const { error } = await supabase.rpc('admin_delete_user', { p_user_id: userId });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value = {
    user,
    profile,
    loading,
    adminExists,
    signIn,
    signOut,
    setupAdmin,
    adminCreateCustomer,
    adminListUsers,
    adminDeleteUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
